import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { authService, UserPermissionsData } from '@/services/auth.service';

// Fetch user permissions from API
const fetchUserPermissions = async (userUId: string): Promise<UserPermissionsData> => {
  const response = await api.admin.post<UserPermissionsData>(
    API_ENDPOINTS.admin.getUserPermissionsByUserUId,
    { uId: userUId }
  );
  return response.data;
};

/**
 * Hook to sync user permissions from API to authService on every page load.
 * This ensures real-time permission values are always available.
 *
 * Call this hook in MainLayout to ensure permissions are fetched on every authenticated page.
 */
export const useUserPermissionsSync = () => {
  const userUId = authService.getUserUid();
  const isCollegeAdmin = authService.isCollegeAdmin();

  // Only fetch permissions for non-admin users
  const shouldFetch = !isCollegeAdmin && !!userUId;

  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['userPermissions', userUId],
    queryFn: () => fetchUserPermissions(userUId!),
    enabled: shouldFetch,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Update authService whenever permissions data changes
  useEffect(() => {
    if (permissions && shouldFetch) {
      authService.setUserPermissions(permissions);
    }
  }, [permissions, shouldFetch]);

  return {
    permissions,
    isLoading: shouldFetch ? isLoading : false,
    error,
    isCollegeAdmin,
  };
};

export default useUserPermissionsSync;
